import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

/**
 * Check if localStorage is available and working
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__storage_test__'
    localStorage.setItem(testKey, testKey)
    localStorage.removeItem(testKey)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Custom storage adapter that handles localStorage quota errors
 * by clearing non-essential cached data when storage is full.
 * Falls back gracefully if localStorage is not available.
 */
const createResilientStorage = () => {
  // Check if localStorage is available at creation time
  const storageAvailable = isLocalStorageAvailable()

  if (!storageAvailable) {
    console.warn(
      '[Storage] localStorage not available, using in-memory fallback'
    )
    // Return an in-memory storage fallback
    const memoryStorage = new Map()
    return {
      getItem: (key) => memoryStorage.get(key) ?? null,
      setItem: (key, value) => memoryStorage.set(key, value),
      removeItem: (key) => memoryStorage.delete(key)
    }
  }

  // Pinia store keys that can be cleared to free space
  const clearableKeys = [
    'storeHomeFeed',
    'storeReviews',
    'storeLists',
    'storeTitleLogs',
    'storeProfile',
    'storeFeed',
    'storeTitlesUser',
    'storeUsers'
  ]

  const clearCacheForSpace = () => {
    console.warn(
      '[Storage] ⚠️ localStorage quota exceeded - auto-clearing cached data to free space'
    )
    let freedBytes = 0
    let clearedKeys = []

    // Clear Pinia store caches (largest data)
    for (const key of clearableKeys) {
      try {
        const value = localStorage.getItem(key)
        if (value) {
          const bytes = value.length * 2 // UTF-16 = 2 bytes per char
          freedBytes += bytes
          localStorage.removeItem(key)
          clearedKeys.push(`${key} (${Math.round(bytes / 1024)}KB)`)
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
    }

    if (clearedKeys.length > 0) {
      console.warn(`[Storage] Cleared cached stores: ${clearedKeys.join(', ')}`)
      console.warn(
        `[Storage] Freed approximately ${Math.round(freedBytes / 1024)}KB - data will be re-fetched on next load`
      )
    }

    return freedBytes > 0
  }

  return {
    getItem: (key) => {
      try {
        return localStorage.getItem(key)
      } catch (e) {
        console.error('[Storage] Error reading from localStorage:', e)
        return null
      }
    },
    setItem: (key, value) => {
      try {
        localStorage.setItem(key, value)
      } catch (e) {
        // Check if it's a quota error
        if (
          e.name === 'QuotaExceededError' ||
          e.code === 22 ||
          e.code === 1014
        ) {
          // Try to free up space and retry
          if (clearCacheForSpace()) {
            try {
              localStorage.setItem(key, value)
              console.warn(
                '[Storage] ✓ Successfully saved after clearing cache'
              )
              return
            } catch (retryError) {
              console.error(
                '[Storage] ⚠️ Still unable to save after clearing cache',
                retryError
              )
            }
          }
        }
        // Don't throw - just log the error to prevent breaking the auth flow
        console.error('[Storage] Error saving to localStorage:', e)
      }
    },
    removeItem: (key) => {
      try {
        localStorage.removeItem(key)
      } catch (e) {
        console.error('[Storage] Error removing from localStorage:', e)
      }
    }
  }
}

// Create Supabase client - don't override storageKey to preserve existing sessions
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: createResilientStorage(),
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// API performance instrumentation: wrap functions.invoke to log slow calls
const _originalInvoke = supabase.functions.invoke.bind(supabase.functions)
supabase.functions.invoke = async function (functionName, options) {
  const start = performance.now()
  try {
    const result = await _originalInvoke(functionName, options)
    const duration = performance.now() - start
    if (duration > 2000) {
      console.warn(
        `[perf] Slow API call: ${functionName} took ${Math.round(duration)}ms`
      )
    }
    return result
  } catch (error) {
    const duration = performance.now() - start
    console.warn(
      `[perf] Failed API call: ${functionName} after ${Math.round(duration)}ms`
    )
    throw error
  }
}

// Debug realtime helper — only available in development, must be called manually
// Usage: import { enableRealtimeDebug } from '@/supabase'; enableRealtimeDebug()
let debugChannel = null
export const enableRealtimeDebug = () => {
  if (import.meta.env.MODE !== 'development') {
    console.warn('[realtime-debug] Only available in development mode')
    return
  }
  if (debugChannel) {
    console.log('[realtime-debug] Already active')
    return
  }
  debugChannel = supabase
    .channel('debug-all-changes')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public' },
      (payload) => console.log(`[realtime-debug] ${payload.eventType}`, payload)
    )
    .subscribe()
  console.log('[realtime-debug] Listening to all public schema changes')
}

export const disableRealtimeDebug = () => {
  if (debugChannel) {
    supabase.removeChannel(debugChannel)
    debugChannel = null
    console.log('[realtime-debug] Stopped')
  }
}
